import {
  ApplyFeatureFlagTypes,
  enforceValidObject,
  isValidArray,
  isValidObject,
  Validate,
  ValidateObject,
} from '@nutrien/ddc-fe-feature-flags-react';
import { isBoolean, isValidRecord, parseNestedJson } from '@nutrien/ddc-fe-util';
import { isString } from 'radash';

import { defaultFeatureFlags } from '@/featureFlags/defaultFeatureFlags';

export interface BannerMessage {
  display: boolean;
  message: string;
}

export interface DataSync {
  allowSync: boolean;
  message: string;
}

export const isBannerMessage: Validate<BannerMessage> = value =>
  isValidObject(
    {
      display: isBoolean,
      message: isString,
    },
    value,
  );

export const isDataSync: Validate<DataSync> = value =>
  isValidObject(
    {
      allowSync: isBoolean,
      message: isString,
    },
    value,
  );

export const isStringRecord: Validate<Record<string, string>> = value =>
  isValidRecord(isString, isString, value);

export const isArrayOfStrings: Validate<string[]> = value => isValidArray(isString, value);

export interface FeatureFlags {
  allowAutoUpdate: boolean;
  borerBlockUpdate: boolean;
  borerDelaysMultiEditMode: boolean;
  hideChevronStartAndEndDistance: boolean;
  borerDisplayCuttingPermitSeverityValue: boolean;
  borerOutageBanner: BannerMessage;
  borerOutageWarningBanner: BannerMessage;
  borerRebuildDatbaseButtonEnabled: boolean;
  borerRefreshButtonEnabled: boolean;
  borerWfaiDate: Record<string, string>;
  displayrxdbsyncindicator: boolean;
  documentsV2: boolean;
  documentsV2Borer: boolean;
  documentsV2Global: boolean;
  documentsV2Location: boolean;
  logSyncState: boolean;
  maxUpdateVersion: string;
  msBorerDataSync: {
    allowSync: boolean;
    message: string;
  };
  passwordlessLogin: boolean;
  rehabBorersVisible: boolean;
  showCloseShiftButton: boolean;
  syncEnhancements: boolean;
  syncFamilies: boolean;
  versionBlacklist: string[];
  equipmentTabVisible: boolean;
  equipmentTabEditable: boolean;
  logAllPushErrors: boolean;
  roomConditionComments: boolean;
  equipmentLocation: boolean;
  swpOperatingCrew: boolean;
}

export const validateFeatureFlags: ValidateObject<FeatureFlags> = {
  allowAutoUpdate: isBoolean,
  borerBlockUpdate: isBoolean,
  borerDelaysMultiEditMode: isBoolean,
  borerDisplayCuttingPermitSeverityValue: isBoolean,
  borerOutageBanner: isBannerMessage,
  borerOutageWarningBanner: isBannerMessage,
  borerRebuildDatbaseButtonEnabled: isBoolean,
  borerRefreshButtonEnabled: isBoolean,
  borerWfaiDate: isStringRecord,
  displayrxdbsyncindicator: isBoolean,
  documentsV2: isBoolean,
  documentsV2Borer: isBoolean,
  documentsV2Global: isBoolean,
  documentsV2Location: isBoolean,
  logSyncState: isBoolean,
  maxUpdateVersion: isString,
  msBorerDataSync: isDataSync,
  passwordlessLogin: isBoolean,
  rehabBorersVisible: isBoolean,
  showCloseShiftButton: isBoolean,
  syncEnhancements: isBoolean,
  syncFamilies: isBoolean,
  versionBlacklist: isArrayOfStrings,
  equipmentTabVisible: isBoolean,
  equipmentTabEditable: isBoolean,
  logAllPushErrors: isBoolean,
  roomConditionComments: isBoolean,
  equipmentLocation: isBoolean,
  hideChevronStartAndEndDistance: isBoolean,
  swpOperatingCrew: isBoolean,
};

export const isFeatureFlags: Validate<FeatureFlags> = value =>
  isValidObject(validateFeatureFlags, value);

export const applyFeatureFlagTypes: ApplyFeatureFlagTypes<FeatureFlags> = generic => {
  const parsedFeatureFlags = parseNestedJson(generic);
  return enforceValidObject(validateFeatureFlags, defaultFeatureFlags, parsedFeatureFlags);
};
